import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type { ICardWalletData, IDocumentRequestPayload, IDocumentsSummary } from "@/types/(my-space)/documents";
import type {
  IDocument,
  IDocumentRequest,
  IDocumentRequestStats,
  IDocumentVersion,
} from "@/types/document";
import { createApi } from "@reduxjs/toolkit/query/react";

export interface MySignedDocumentRow {
  id: number;
  documentId: number;
  documentType: string | null;
  description: string | null;
  signedAt: string | null;
  hasSignedPdf: boolean;
}

export const documentsApi = createApi({
  reducerPath: "documentsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getDocuments",
    "getSentDocumentsRequests",
    "getRecievedDocumentsRequests",
    "getDocumentsSummary",
    "DocumentRequestById",
    "getCardWallet",
    "MySignedDocuments",
    "msDocRequestStats",
    "MyDocumentById",
    "MyDocumentVersions",
  ],
  endpoints: (builder) => ({
    getCardWallet: builder.query<IApiResponse<ICardWalletData>, void>({
      query: () => ({
        url: "/employee/documents/card-wallet",
      }),
      providesTags: ["getCardWallet"],
    }),
    getDocumentsSummary: builder.query<IApiResponse<IDocumentsSummary>, void>({
      query: () => ({
        url: "/employee/documents/counts",
      }),
      providesTags: ["getDocumentsSummary"],
    }),
    getMyDocuments: builder.query<IApiResponse<IDocument[]>, {
      documentView?: "All" | "Card";
      employeeDocumentType?: string;
      // The My Documents table paginates, searches and filters server-side.
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }>({
      query: (params) => ({
        url: "/employee/documents",
        params,
      }),
      providesTags: ["getDocuments"],
    }),
    getMyDocumentById: builder.query<IApiResponse<IDocument>, string>({
      query: (id) => ({
        url: `/employee/documents/by-id/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "MyDocumentById", id }],
    }),
    getMyDocumentVersions: builder.query<IApiResponse<IDocumentVersion[]>, string>({
      query: (id) => ({
        url: `/employee/documents/${id}/versions`,
      }),
      providesTags: (_result, _error, id) => [{ type: "MyDocumentVersions", id }],
    }),
    // Submits the update for HR review; the document itself only changes
    // once HR approves.
    updateMyDocument: builder.mutation<
      IApiResponse<IDocument>,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `/employee/documents/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "getDocuments",
        "getDocumentsSummary",
        "getCardWallet",
        // The update files a review request under the employee's Sent Requests.
        "getSentDocumentsRequests",
        "msDocRequestStats",
        { type: "MyDocumentById", id },
        { type: "MyDocumentVersions", id },
      ],
    }),
    getSentDocumentsRequests: builder.query<IApiResponse<IDocumentRequest[]>, any>({
      query: (params) => ({
        url: "/employee/document-requests/sent-requests",
        params,
      }),
      providesTags: ["getSentDocumentsRequests"],
    }),
    getRecievedDocumentsRequests: builder.query<IApiResponse<IDocumentRequest[]>, any>({
      query: (params) => ({
        url: "/employee/document-requests/received-requests",
        params,
      }),
      providesTags: ["getRecievedDocumentsRequests"],
    }),
    getMsDocumentRequestStats: builder.query<
      IApiResponse<IDocumentRequestStats>,
      { type: "sent" | "received" }
    >({
      query: ({ type }) => ({
        url: "/employee/document-requests/requests-stats",
        method: "GET",
        params: { type },
      }),
      providesTags: ["msDocRequestStats"],
    }),
    cancelSentDocumentRequest: builder.mutation<IApiResponse<void>, any>({
      query: (id) => ({
        url: `/employee/document-requests/${id}/cancel`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, id) => ["getSentDocumentsRequests", "msDocRequestStats", { type: "DocumentRequestById", id }],
    }),
    reopenSentDocumentRequest: builder.mutation<IApiResponse<void>, any>({
      query: (id) => ({
        url: `/employee/document-requests/${id}/reopen`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, id) => ["getSentDocumentsRequests", "msDocRequestStats", { type: "DocumentRequestById", id }],
    }),
    createDocumentRequest: builder.mutation<IApiResponse<IDocumentRequest>, IDocumentRequestPayload>({
      query: (data) => ({
        url: "/employee/document-requests",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["getSentDocumentsRequests", "getDocumentsSummary", "msDocRequestStats"],
    }),
    updateDocumentRequest: builder.mutation<
      IApiResponse<IDocumentRequest>,
      { id: string; data: IDocumentRequestPayload }
    >({
      query: ({ id, data }) => ({
        url: `/employee/document-requests/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ["getSentDocumentsRequests", { type: "DocumentRequestById", id }],
    }),
    uploadDocument: builder.mutation<IApiResponse<void>, FormData>({
      query: (data) => ({
        url: "/employee/documents",
        method: "POST",
        body: data,
      }),
      // A self-upload carries no requestId — it still has to refresh the My
      // Documents table and the summary counts above it.
      invalidatesTags: (_result, _error, formData) => [
        "getDocuments",
        "getDocumentsSummary",
        "getCardWallet",
        "getRecievedDocumentsRequests",
        "msDocRequestStats",
        {
          type: "DocumentRequestById",
          id: formData.get("requestId") as string,
        },
      ],
    }),
    rejectDocumentRequest: builder.mutation<IApiResponse<void>, { id: number; rejectionReason: string }>({
      query: ({ id, rejectionReason }) => ({
        url: `/employee/document-requests/${id}/reject`,
        method: "PUT",
        body: { rejectionReason },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "getRecievedDocumentsRequests",
        "msDocRequestStats",
        { type: "DocumentRequestById", id },
      ],
    }),
    getDocumentRequestById: builder.query<IApiResponse<IDocumentRequest>, string>({
      query: (requestId) => ({
        url: `/employee/document-requests/${requestId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, requestId) => [{ type: "DocumentRequestById", id: requestId }],
    }),
    getMySignedDocuments: builder.query<IApiResponse<MySignedDocumentRow[]>, void>({
      query: () => ({ url: "/employee/documents/signed-documents" }),
      providesTags: ["MySignedDocuments"],
    }),
    getMySignedPdfUrl: builder.query<
      IApiResponse<{ url: string; hash: string | null; signedAt: string | null }>,
      { signatureId: number }
    >({
      query: ({ signatureId }) => ({
        url: `/employee/documents/signed-documents/${signatureId}`,
      }),
    }),
  }),
});

export const {
  useGetCardWalletQuery,
  useGetMyDocumentsQuery,
  useGetMyDocumentByIdQuery,
  useGetMyDocumentVersionsQuery,
  useUpdateMyDocumentMutation,
  useGetDocumentsSummaryQuery,
  useGetSentDocumentsRequestsQuery,
  useGetRecievedDocumentsRequestsQuery,
  useGetMsDocumentRequestStatsQuery,
  useCancelSentDocumentRequestMutation,
  useReopenSentDocumentRequestMutation,
  useCreateDocumentRequestMutation,
  useUpdateDocumentRequestMutation,
  useUploadDocumentMutation,
  useGetDocumentRequestByIdQuery,
  useRejectDocumentRequestMutation,
  useGetMySignedDocumentsQuery,
  useLazyGetMySignedPdfUrlQuery,
} = documentsApi;
